'use client'

import { useCallback, useMemo } from 'react'
import { useTranslations } from 'next-intl'
import { formatCurrency } from '@/src/utils/currency/formatCurrency'
import {
    formatCleartripTransactionsForView,
    formatFlyinTransactionsForView
} from '@/src/utils/wallet/transactionViewFormatters'
import type { UseWalletReturn } from './useWallet'

export interface UseWalletContentDataOptions {
    wallet: UseWalletReturn
    locale: string
}

export interface UseWalletContentDataReturn {
    formattedTransactions: ReturnType<typeof formatFlyinTransactionsForView>
    cleartripFormattedTransactions: ReturnType<typeof formatCleartripTransactionsForView>
    translatedBalanceError: string | null
    translatedTransactionsError: string | null
    formatAmount: (amount: number, currency: string) => string
    cleartripTransactionLabels: {
        transactionHistoryLabel: string
        noTransactionsLabel: string
        loadMoreLabel: string
        loadingLabel: string
    }
}

/**
 * Shared hook for wallet content data used by both desktop and mobile wallet pages.
 * Centralizes formatted transactions, error translations, formatAmount, and Cleartrip labels.
 */
export function useWalletContentData({
    wallet,
    locale
}: UseWalletContentDataOptions): UseWalletContentDataReturn {
    const t = useTranslations('wallet')

    const formatAmount = useCallback(
        (amount: number, currency: string) =>
            formatCurrency({
                amount,
                currency,
                locale,
                precision: 2
            }),
        [locale]
    )

    const formattedTransactions = useMemo(() => {
        if (wallet.transactions.length === 0) return []
        return formatFlyinTransactionsForView(wallet.transactions, t)
    }, [wallet.transactions, t])

    const cleartripFormattedTransactions = useMemo(() => {
        if (wallet.transactions.length === 0) return []
        return formatCleartripTransactionsForView(wallet.transactions, t, locale)
    }, [wallet.transactions, t, locale])

    const translatedBalanceError = useMemo(() => {
        if (!wallet.balanceError) return null
        if (wallet.balanceError === 'Unable to fetch wallet balance') {
            return t('errors.balanceFetchFailed')
        }
        return wallet.balanceError
    }, [wallet.balanceError, t])

    const translatedTransactionsError = useMemo(() => {
        if (!wallet.transactionsError) return null
        if (wallet.transactionsError === 'Unable to fetch transactions') {
            return t('errors.transactionsFetchFailed')
        }
        return wallet.transactionsError
    }, [wallet.transactionsError, t])

    const cleartripTransactionLabels = useMemo(
        () => ({
            transactionHistoryLabel: t('transactionHistory'),
            noTransactionsLabel: t('noTransactions'),
            loadMoreLabel: t('loadMoreTransactions'),
            loadingLabel: t('loading')
        }),
        [t]
    )

    return {
        formattedTransactions,
        cleartripFormattedTransactions,
        translatedBalanceError,
        translatedTransactionsError,
        formatAmount,
        cleartripTransactionLabels
    }
}
